'use client'
// =============================================================================
// LandingView.tsx — Amiral Blanc · accueil public (sans compte)
//
// Ce que la page doit faire comprendre en une minute : Amiral Blanc n'est ni un
// simple robot conversationnel ni un ERP de plus — c'est un environnement de
// travail qui transforme l'information dispersée en contexte, en actions et en
// résultats, avec des agents IA sous contrôle humain.
//
// Contenu des modules : src/lib/presentation/modules.ts (même liste que l'accueil
// connecté et la fenêtre de bienvenue). Chaque module affiche son ÉTAT RÉEL —
// en service / pilote / feuille de route (docs/product/presentation-claims-control.md).
// =============================================================================
import { useEffect, useRef, useState } from 'react'
import Link from 'next/link'

import {
  MODULES, STATE_LABEL, GROUP_LABEL, PRINCIPLE, START_PATH, TAGLINE, SUBLINE,
  type ModuleGroup, type ModuleState,
} from '@/lib/presentation/modules'

const CYAN = '#00d4ff'

function useReveal(threshold = 0.1) {
  const ref = useRef<HTMLDivElement>(null)
  const [visible, setVisible] = useState(false)
  useEffect(() => {
    const el = ref.current
    if (!el) return
    const obs = new IntersectionObserver(([e]) => { if (e.isIntersecting) { setVisible(true); obs.disconnect() } }, { threshold })
    obs.observe(el)
    return () => obs.disconnect()
  }, [threshold])
  return { ref, visible }
}

function Particles({ count = 30 }: { count?: number }) {
  const items = useRef(
    Array.from({ length: count }, () => ({
      x: Math.random() * 100, y: Math.random() * 100, size: 1 + Math.random() * 2,
      opacity: 0.06 + Math.random() * 0.14, dur: 8 + Math.random() * 12, delay: Math.random() * 8,
    })),
  )
  return (
    <div aria-hidden style={{ position: 'absolute', inset: 0, pointerEvents: 'none', overflow: 'hidden' }}>
      {items.current.map((p, i) => (
        <div key={i} style={{
          position: 'absolute', left: `${p.x}%`, top: `${p.y}%`, width: p.size, height: p.size, borderRadius: '50%',
          background: CYAN, opacity: p.opacity, animation: `float-node ${p.dur}s ${p.delay}s ease-in-out infinite alternate`,
        }} />
      ))}
    </div>
  )
}

/** Section qui apparaît au défilement, avec sur-titre et titre. */
function Section({ kicker, title, intro, children }: { kicker: string; title: React.ReactNode; intro?: React.ReactNode; children: React.ReactNode }) {
  const { ref, visible } = useReveal()
  return (
    <section ref={ref} className="lp-section">
      <div style={{ maxWidth: 1180, margin: '0 auto', opacity: visible ? 1 : 0, transform: visible ? 'none' : 'translateY(18px)', transition: 'opacity 0.6s, transform 0.6s' }}>
        <div className="lp-kicker">{kicker}</div>
        <h2 className="lp-h2">{title}</h2>
        {intro && <p className="lp-intro">{intro}</p>}
        {children}
      </div>
    </section>
  )
}

function StateBadge({ state }: { state: ModuleState }) {
  const s = STATE_LABEL[state]
  return (
    <span title={s.hint} style={{
      fontFamily: 'var(--font-mono)', fontSize: 9, letterSpacing: '0.1em', textTransform: 'uppercase', whiteSpace: 'nowrap',
      color: s.color, border: `1px solid ${s.color}55`, borderRadius: 3, padding: '2px 6px',
    }}>{s.label}</span>
  )
}

// ─── Navigation ───────────────────────────────────────────────────────────────

function LandingNav() {
  const [scrolled, setScrolled] = useState(false)
  useEffect(() => {
    const fn = () => setScrolled(window.scrollY > 40)
    window.addEventListener('scroll', fn, { passive: true })
    return () => window.removeEventListener('scroll', fn)
  }, [])
  return (
    <nav className="lp-nav" style={{
      background: scrolled ? 'rgba(2,4,8,0.88)' : 'transparent',
      borderBottom: scrolled ? '1px solid rgba(0,212,255,0.12)' : '1px solid transparent',
      backdropFilter: scrolled ? 'blur(20px)' : 'none',
    }}>
      <span style={{ fontFamily: 'var(--font-display)', fontSize: 16, fontWeight: 800, color: '#fff', letterSpacing: '-0.02em' }}>
        Amiral Blanc<span style={{ color: CYAN }}>.</span>
      </span>
      <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
        <a href="#modules" className="lp-link lp-hide-sm">Modules</a>
        <a href="#exemple" className="lp-link lp-hide-sm">Exemple</a>
        <Link href="/login" className="lp-btn-ghost">Connexion</Link>
        <Link href="/?os=1" className="lp-btn">Découvrir →</Link>
      </div>
    </nav>
  )
}

// ─── Hero ─────────────────────────────────────────────────────────────────────

const FLOW = ['Information', 'Contexte', 'Compréhension', 'Proposition', 'Validation', 'Action', 'Résultat']

function Hero() {
  return (
    <section style={{ position: 'relative', minHeight: '92dvh', display: 'flex', alignItems: 'center', overflow: 'hidden', padding: '110px 16px 40px' }}>
      <div aria-hidden style={{ position: 'absolute', inset: 0, pointerEvents: 'none' }}>
        <div style={{ position: 'absolute', top: '35%', left: '50%', transform: 'translate(-50%,-50%)', width: 900, maxWidth: '120vw', height: 600, borderRadius: '50%', background: 'radial-gradient(ellipse, rgba(0,212,255,0.1) 0%, rgba(192,132,252,0.06) 40%, transparent 70%)', filter: 'blur(60px)', animation: 'glow-pulse 9s ease-in-out infinite' }} />
        <div style={{ position: 'absolute', inset: 0, backgroundImage: 'linear-gradient(rgba(0,212,255,0.018) 1px, transparent 1px), linear-gradient(90deg, rgba(0,212,255,0.018) 1px, transparent 1px)', backgroundSize: '72px 72px', maskImage: 'radial-gradient(ellipse at center, black 10%, transparent 75%)' }} />
        <Particles count={26} />
      </div>

      <div className="lp-hero-grid">
        <div>
          <div className="lp-kicker" style={{ animation: 'fadeUp 0.6s ease-out' }}>Amiral Blanc</div>
          <h1 className="lp-h1" style={{ animation: 'fadeUp 0.7s 0.1s ease-out both' }}>
            {TAGLINE.replace(/\.$/, '')}
            <span style={{ color: CYAN }}>.</span>
          </h1>
          <p className="lp-lead" style={{ animation: 'fadeUp 0.7s 0.25s ease-out both' }}>{SUBLINE}</p>
          <p className="lp-lead" style={{ fontSize: 13, color: 'rgba(255,255,255,0.4)', animation: 'fadeUp 0.7s 0.35s ease-out both' }}>
            CRM, projets, documents, communications, tâches et agents IA dans un même environnement — pour un Bureau, une PME ou une organisation.
          </p>
          <div style={{ display: 'flex', gap: 10, flexWrap: 'wrap', animation: 'fadeUp 0.7s 0.45s ease-out both' }}>
            <Link href="/login" className="lp-btn lp-btn-lg">Créer mon premier projet →</Link>
            <Link href="/?os=1" className="lp-btn-ghost lp-btn-lg">Découvrir sans compte</Link>
          </div>
        </div>

        {/* Le principe, puis le chemin d'une information */}
        <div className="lp-card" style={{ animation: 'fadeUp 0.8s 0.4s ease-out both' }}>
          <div className="lp-mono-label">LE PRINCIPE</div>
          {PRINCIPLE.map((p, i) => (
            <div key={p} style={{ fontFamily: 'var(--font-display)', fontSize: 'clamp(20px, 2.4vw, 28px)', fontWeight: 700, color: i === 1 ? CYAN : '#fff', lineHeight: 1.25 }}>{p}</div>
          ))}
          <div className="lp-mono-label" style={{ marginTop: 22 }}>LE CHEMIN D’UNE INFORMATION</div>
          <div style={{ display: 'flex', flexWrap: 'wrap', gap: 6, alignItems: 'center' }}>
            {FLOW.map((f, i) => (
              <span key={f} style={{ display: 'inline-flex', alignItems: 'center', gap: 6 }}>
                <span style={{ fontFamily: 'var(--font-mono)', fontSize: 11, color: f === 'Validation' ? CYAN : 'rgba(255,255,255,0.75)', border: `1px solid ${f === 'Validation' ? CYAN + '66' : 'rgba(255,255,255,0.12)'}`, borderRadius: 4, padding: '4px 8px' }}>{f}</span>
                {i < FLOW.length - 1 && <span style={{ color: 'rgba(255,255,255,0.25)' }}>→</span>}
              </span>
            ))}
          </div>
          <p style={{ fontFamily: 'var(--font-mono)', fontSize: 11, color: 'rgba(255,255,255,0.4)', lineHeight: 1.7, marginTop: 16, marginBottom: 0 }}>
            Rien d’irréversible ne part sans votre accord : une proposition attend toujours la validation de la personne responsable.
          </p>
        </div>
      </div>
    </section>
  )
}

// ─── Raison d'être ────────────────────────────────────────────────────────────

const SCATTER = [
  { where: 'dans une boîte de réception', what: 'Un courriel' },
  { where: 'sur un ordinateur',           what: 'Un PDF' },
  { where: 'dans un autre outil',         what: 'Une tâche' },
  { where: 'dans une conversation',       what: 'Une décision' },
  { where: 'dans un fichier Excel',       what: 'Un client' },
  { where: 'dans la tête d’une personne', what: 'Une procédure' },
]

function WhySection() {
  return (
    <Section kicker="Raison d’être" title={<>Le problème n’est pas le manque d’information.<br /><span style={{ color: 'rgba(255,255,255,0.4)' }}>C’est qu’elle est dispersée.</span></>}>
      <div className="lp-grid-3">
        {SCATTER.map(s => (
          <div key={s.what} className="lp-tile">
            <div style={{ fontFamily: 'var(--font-display)', fontSize: 16, fontWeight: 700, color: '#fff' }}>{s.what}</div>
            <div style={{ fontFamily: 'var(--font-mono)', fontSize: 11, color: 'rgba(255,255,255,0.45)' }}>{s.where}</div>
          </div>
        ))}
      </div>
      <p className="lp-intro" style={{ marginTop: 24 }}>
        Amiral Blanc réunit tout cela autour du <strong style={{ color: '#fff' }}>travail réel à accomplir</strong> : chaque courriel, fichier ou
        message est relié à son client, son entreprise et son projet — puis des agents l’analysent et préparent la suite.
        <br /><span style={{ color: CYAN }}>De l’information dispersée → au travail coordonné → à l’amélioration continue.</span>
      </p>
    </Section>
  )
}

// ─── Modules (une seule source, état réel) ────────────────────────────────────

function ModulesSection() {
  const groups: ModuleGroup[] = ['travail', 'agents', 'intelligence']
  return (
    <section id="modules" className="lp-section">
      <div style={{ maxWidth: 1180, margin: '0 auto' }}>
        <div className="lp-kicker">Un environnement, pas une pile d’outils</div>
        <h2 className="lp-h2">Ce qu’Amiral Blanc fait aujourd’hui — et ce qui vient.</h2>
        <div style={{ display: 'flex', gap: 14, flexWrap: 'wrap', margin: '0 0 22px' }}>
          {(Object.keys(STATE_LABEL) as ModuleState[]).map(s => (
            <span key={s} style={{ display: 'inline-flex', gap: 6, alignItems: 'center', fontFamily: 'var(--font-mono)', fontSize: 11, color: 'rgba(255,255,255,0.5)' }}>
              <StateBadge state={s} /> {STATE_LABEL[s].hint}
            </span>
          ))}
        </div>
        {groups.map(g => (
          <div key={g} style={{ marginBottom: 28 }}>
            <div className="lp-mono-label" style={{ color: 'rgba(255,255,255,0.55)' }}>{GROUP_LABEL[g].toUpperCase()}</div>
            <div className="lp-grid-3">
              {MODULES.filter(m => m.group === g).map(m => (
                <div key={m.key} className="lp-tile" style={{ opacity: m.state === 'route' ? 0.75 : 1 }}>
                  <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                    <span style={{ fontSize: 16, color: CYAN }}>{m.icon}</span>
                    <strong style={{ fontFamily: 'var(--font-display)', fontSize: 15, color: '#fff' }}>{m.name}</strong>
                    <span style={{ marginLeft: 'auto' }}><StateBadge state={m.state} /></span>
                  </div>
                  <div style={{ fontSize: 13, color: 'rgba(255,255,255,0.7)', lineHeight: 1.5 }}>{m.pitch}</div>
                  <ul style={{ margin: 0, paddingLeft: 16, fontFamily: 'var(--font-mono)', fontSize: 11, color: 'rgba(255,255,255,0.45)', lineHeight: 1.7 }}>
                    {m.points.map(p => <li key={p}>{p}</li>)}
                  </ul>
                </div>
              ))}
            </div>
          </div>
        ))}
      </div>
    </section>
  )
}

// ─── Exemple concret ──────────────────────────────────────────────────────────

const EXAMPLE: { step: string; detail: string; state: ModuleState }[] = [
  { step: 'Créer le projet « Pub-Test »',             detail: 'But : présenter nos services à des clients potentiels. Objectif : 15 appels en 30 jours.', state: 'service' },
  { step: 'Déposer echantillon.pdf sur son poste',    detail: 'Le dossier synchronisé l’envoie au Bureau, avec sa provenance.',                           state: 'service' },
  { step: 'L’agent détecte et analyse le document',   detail: 'Sur le poste si possible, sinon le serveur prend le relais.',                              state: 'pilote' },
  { step: 'Il propose un plan, à partir du but',      detail: 'Préparer le message, choisir les contacts, joindre le PDF — avec ses sources.',            state: 'route' },
  { step: 'Vous validez, modifiez ou refusez',        detail: 'Le but donne le contexte ; il n’autorise jamais l’envoi à lui seul.',                       state: 'pilote' },
  { step: 'Envoi, suivi et résultats',                detail: 'Réponses et rendez-vous comparés à l’objectif ; la prochaine action est proposée.',        state: 'route' },
]

function ExampleSection() {
  return (
    <section id="exemple" className="lp-section">
      <div style={{ maxWidth: 980, margin: '0 auto' }}>
        <div className="lp-kicker">Un exemple concret</div>
        <h2 className="lp-h2">D’un PDF à une campagne contrôlée et traçable.</h2>
        <p className="lp-intro">Chaque étape porte son état réel : ce qui fonctionne aujourd’hui, et ce qui est en construction.</p>
        <ol style={{ listStyle: 'none', padding: 0, margin: 0 }}>
          {EXAMPLE.map((e, i) => (
            <li key={e.step} style={{ display: 'flex', gap: 14, padding: '14px 0', borderBottom: '1px solid rgba(255,255,255,0.06)' }}>
              <span style={{ fontFamily: 'var(--font-mono)', fontSize: 12, color: CYAN, width: 22, flexShrink: 0 }}>{String(i + 1).padStart(2, '0')}</span>
              <div style={{ flex: 1, minWidth: 0 }}>
                <div style={{ display: 'flex', gap: 8, alignItems: 'center', flexWrap: 'wrap' }}>
                  <strong style={{ color: '#fff', fontSize: 15 }}>{e.step}</strong>
                  <StateBadge state={e.state} />
                </div>
                <div style={{ fontFamily: 'var(--font-mono)', fontSize: 12, color: 'rgba(255,255,255,0.5)', marginTop: 4, lineHeight: 1.6 }}>{e.detail}</div>
              </div>
            </li>
          ))}
        </ol>
      </div>
    </section>
  )
}

// ─── Postes et agents ─────────────────────────────────────────────────────────

function AgentsSection() {
  const items = [
    { t: 'Détecter',  d: 'Un fichier, un courriel, un changement sur le poste.' },
    { t: 'Analyser',  d: 'Avec les capacités du poste, ou celles du serveur.' },
    { t: 'Proposer',  d: 'Un classement, une réponse, un plan — avec ses sources.' },
    { t: 'Valider',   d: 'Par la personne responsable, à la hauteur du risque.' },
    { t: 'Exécuter',  d: 'Seulement ce qui a été validé, et tout est tracé.' },
  ]
  return (
    <Section kicker="Vos ordinateurs deviennent des agents de travail"
      title={<>Un agent n’est pas une autorisation illimitée.</>}
      intro={<>Ce que l’agent partage avec le Bureau se limite à son dossier synchronisé. Toute capacité plus large
        (accéder à d’autres fichiers, diagnostiquer le réseau, utiliser la carte graphique…) doit être <strong style={{ color: '#fff' }}>approuvée par vous</strong>,
        et les décisions sont journalisées.</>}>
      <div className="lp-grid-5">
        {items.map((it, i) => (
          <div key={it.t} className="lp-tile" style={{ borderColor: it.t === 'Valider' ? CYAN + '55' : undefined }}>
            <div style={{ fontFamily: 'var(--font-mono)', fontSize: 10, color: 'rgba(255,255,255,0.35)' }}>{String(i + 1).padStart(2, '0')}</div>
            <div style={{ fontFamily: 'var(--font-display)', fontSize: 16, fontWeight: 700, color: it.t === 'Valider' ? CYAN : '#fff' }}>{it.t}</div>
            <div style={{ fontSize: 12, color: 'rgba(255,255,255,0.55)', lineHeight: 1.5 }}>{it.d}</div>
          </div>
        ))}
      </div>
    </Section>
  )
}

// ─── Du CRM à l'ERP ───────────────────────────────────────────────────────────

function ErpSection() {
  const chain = ['Processus', 'Observation', 'Mesures', 'Problèmes', 'Causes', 'Actions', 'Tâches', 'Résultats', 'Amélioration']
  return (
    <Section kicker="Du CRM à l’ERP — sur notre feuille de route"
      title={<>Comprendre pourquoi un processus bloque, agir, et mesurer le résultat.</>}
      intro={<>Le projet deviendra le point de départ de l’amélioration des opérations : cartographie du flux de valeur,
        5 Pourquoi, actions et indicateurs — chaque constat produisant une tâche et une mesure. Les mesures restent celles
        que vous définissez : aucune « norme » inventée.</>}>
      <div style={{ display: 'flex', flexWrap: 'wrap', gap: 6, alignItems: 'center' }}>
        {chain.map((c, i) => (
          <span key={c} style={{ display: 'inline-flex', alignItems: 'center', gap: 6 }}>
            <span style={{ fontFamily: 'var(--font-mono)', fontSize: 12, color: 'rgba(255,255,255,0.7)', border: '1px dashed rgba(255,255,255,0.18)', borderRadius: 4, padding: '5px 9px' }}>{c}</span>
            {i < chain.length - 1 && <span style={{ color: 'rgba(255,255,255,0.25)' }}>→</span>}
          </span>
        ))}
      </div>
    </Section>
  )
}

// ─── Commencer ────────────────────────────────────────────────────────────────

function StartSection() {
  const { ref, visible } = useReveal()
  return (
    <section ref={ref} className="lp-section" style={{ textAlign: 'center', position: 'relative', overflow: 'hidden' }}>
      <div aria-hidden style={{ position: 'absolute', top: '50%', left: '50%', transform: 'translate(-50%,-50%)', width: 700, maxWidth: '120vw', height: 400, background: 'radial-gradient(ellipse, rgba(0,212,255,0.1) 0%, rgba(192,132,252,0.06) 40%, transparent 70%)', filter: 'blur(50px)' }} />
      <div style={{ position: 'relative', maxWidth: 760, margin: '0 auto', opacity: visible ? 1 : 0, transform: visible ? 'none' : 'translateY(18px)', transition: 'opacity 0.6s, transform 0.6s' }}>
        <div className="lp-kicker">Commencer simplement</div>
        <h2 className="lp-h2">Vous n’avez pas à tout automatiser le premier jour.</h2>
        <div style={{ display: 'flex', flexWrap: 'wrap', gap: 8, justifyContent: 'center', alignItems: 'center', margin: '8px 0 18px' }}>
          {START_PATH.map((s, i) => (
            <span key={s} style={{ display: 'inline-flex', alignItems: 'center', gap: 8 }}>
              <span style={{ fontFamily: 'var(--font-display)', fontSize: 18, fontWeight: 700, color: '#fff' }}>{s}</span>
              {i < START_PATH.length - 1 && <span style={{ color: CYAN }}>→</span>}
            </span>
          ))}
        </div>
        <p className="lp-intro" style={{ margin: '0 auto 28px' }}>
          Puis ajoutez au rythme de votre équipe : CRM, communications, documents, automatisations, indicateurs.
          Amiral Blanc grandit avec votre façon de travailler.
        </p>
        <div style={{ display: 'flex', gap: 10, justifyContent: 'center', flexWrap: 'wrap' }}>
          <Link href="/login" className="lp-btn lp-btn-lg">Créer mon premier projet →</Link>
          <Link href="/?os=1" className="lp-btn-ghost lp-btn-lg">Découvrir sans compte</Link>
        </div>
        <p style={{ fontFamily: 'var(--font-mono)', fontSize: 10, color: 'rgba(255,255,255,0.3)', lineHeight: 1.7, marginTop: 28 }}>
          Amiral Blanc ne remplace ni un urbaniste, ni un avocat, ni un conseiller financier : il retrouve l’information et sa source, et le dit quand il ne peut pas la prouver.
          Les modules « pilote » et « feuille de route » sont indiqués comme tels.
        </p>
      </div>
    </section>
  )
}

// ─── CSS ──────────────────────────────────────────────────────────────────────

const LANDING_CSS = `
  @keyframes glow-pulse { 0%,100% { opacity:.6 } 50% { opacity:1 } }
  @keyframes fadeUp     { from { opacity:0;transform:translateY(20px) } to { opacity:1;transform:translateY(0) } }
  @keyframes float-node { from { transform:translateY(0) } to { transform:translateY(-12px) } }

  * { box-sizing:border-box }
  .landing-root { background:#020408; color:#d0dde8; min-height:100dvh; overflow-x:hidden; }
  .landing-divider { height:1px; background:linear-gradient(90deg, transparent, rgba(0,212,255,0.12), transparent); margin:0 16px; }

  .lp-nav { position:fixed; top:0; left:0; right:0; z-index:100; padding:12px 16px; display:flex; align-items:center;
            justify-content:space-between; gap:12px; transition:all .35s; }
  .lp-link { font-family:var(--font-mono); font-size:11px; color:rgba(255,255,255,0.5); text-decoration:none; padding:8px 10px; }
  .lp-link:hover { color:#fff }
  .lp-btn, .lp-btn-ghost { font-family:var(--font-mono); font-size:11px; font-weight:700; letter-spacing:.06em; text-decoration:none;
            padding:8px 14px; border-radius:4px; display:inline-block; transition:all .2s; white-space:nowrap; }
  .lp-btn { color:#020408; background:${CYAN}; box-shadow:0 0 20px rgba(0,212,255,0.3); }
  .lp-btn:hover { box-shadow:0 0 34px rgba(0,212,255,0.55); transform:translateY(-1px) }
  .lp-btn-ghost { color:rgba(255,255,255,0.65); border:1px solid rgba(255,255,255,0.15); }
  .lp-btn-ghost:hover { color:#fff; border-color:rgba(255,255,255,0.35) }
  .lp-btn-lg { font-size:12px; padding:13px 22px }

  .lp-hero-grid { position:relative; z-index:1; width:100%; max-width:1180px; margin:0 auto; display:grid;
            grid-template-columns:repeat(auto-fit, minmax(min(100%, 420px), 1fr)); gap:48px; align-items:center; }
  .lp-h1 { font-family:var(--font-display); font-size:clamp(36px, 5.2vw, 68px); font-weight:800; line-height:1.03;
            letter-spacing:-0.035em; color:#fff; margin:0 0 22px; }
  .lp-lead { font-family:var(--font-mono); font-size:14px; color:rgba(255,255,255,0.6); line-height:1.8; max-width:560px; margin:0 0 18px; }
  .lp-card { background:rgba(2,4,8,0.85); border:1px solid rgba(0,212,255,0.15); border-radius:10px; padding:24px;
            box-shadow:0 40px 90px rgba(0,0,0,0.6), 0 0 80px -20px rgba(0,212,255,0.2); }
  .lp-mono-label { font-family:var(--font-mono); font-size:10px; letter-spacing:.18em; color:rgba(0,212,255,0.7); margin-bottom:10px; }

  .lp-section { padding:72px 16px; }
  .lp-kicker { font-family:var(--font-mono); font-size:10px; letter-spacing:.2em; text-transform:uppercase; color:rgba(0,212,255,0.75); margin-bottom:14px; }
  .lp-h2 { font-family:var(--font-display); font-size:clamp(26px, 3.6vw, 44px); font-weight:700; letter-spacing:-0.02em; color:#fff; line-height:1.12; margin:0 0 18px; }
  .lp-intro { font-family:var(--font-mono); font-size:13px; color:rgba(255,255,255,0.55); line-height:1.8; max-width:760px; margin:0 0 26px; }
  .lp-grid-3 { display:grid; grid-template-columns:repeat(auto-fill, minmax(min(100%, 300px), 1fr)); gap:12px; }
  .lp-grid-5 { display:grid; grid-template-columns:repeat(auto-fill, minmax(min(100%, 190px), 1fr)); gap:12px; }
  .lp-tile { display:flex; flex-direction:column; gap:8px; padding:16px; border-radius:8px;
            border:1px solid rgba(255,255,255,0.08); background:rgba(255,255,255,0.02); min-width:0; }
  @media (max-width: 640px) { .lp-hide-sm { display:none } .lp-section { padding:52px 16px } }
`

// ─── Export ───────────────────────────────────────────────────────────────────

export default function LandingView() {
  return (
    <div className="landing-root">
      <style dangerouslySetInnerHTML={{ __html: LANDING_CSS }} />
      <LandingNav />
      <Hero />
      <div className="landing-divider" />
      <WhySection />
      <div className="landing-divider" />
      <ModulesSection />
      <div className="landing-divider" />
      <ExampleSection />
      <div className="landing-divider" />
      <AgentsSection />
      <div className="landing-divider" />
      <ErpSection />
      <StartSection />
    </div>
  )
}
